import React, { useState, useEffect, useRef } from "react";
import Header from "../components/developer_header.jsx";
import Footer from "../components/developer_footer.jsx";
import DevelopersLogin from "../../assets/images/developers_login.png";
import DevelopersSmsOtp from "../../assets/images/developers_sms_otp.png";
import DevelopersHamburgerMenu from "../../assets/images/developers_hamburger_menu.png";
import ResourceDownloadsSidebar from "../../assets/images/resource_downloads_sidebar.png";
import NetsClickAddBankCard from "../../assets/images/nets_click_add_bank_card.png";
import DevelopersSandbox from "../../assets/images/developers_sandbox.png"
import SandboxProjectDetails from "../../assets/images/sandbox_project_details.png"
import SandboxProject from "../../assets/images/sandbox_project.png"
import ResourcePolyFinTechPaytechNetsClick from "../../assets/images/resource_polyfintech_paytech_nets_click.png"

const NetsClickIdeationLayout = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Add state for image modal
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageSrc: ""
  });

  // Function to open image modal
  const openImageModal = (imageSrc) => {
    setImageModal({
      isOpen: true,
      imageSrc: imageSrc
    });
  };
  
  // Function to close image modal
  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      imageSrc: ""
    });
  };
  
  // Create refs for each step section
  const step1Ref = useRef(null);
  const step2Ref = useRef(null);
  const step3Ref = useRef(null);
  const step4Ref = useRef(null);

  // Setup intersection observer to detect which step is in view
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "-100px 0px -60% 0px", // Adjust these values as needed
      threshold: 0
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const stepId = parseInt(entry.target.getAttribute('data-step'));
          setActiveStep(stepId);
        }
      });
    }, options);
    
    // Observe all step elements
    if (step1Ref.current) observer.observe(step1Ref.current);
    if (step2Ref.current) observer.observe(step2Ref.current);
    if (step3Ref.current) observer.observe(step3Ref.current);
    if (step4Ref.current) observer.observe(step4Ref.current);

    return () => {
      // Clean up observer on component unmount
      if (step1Ref.current) observer.unobserve(step1Ref.current);
      if (step2Ref.current) observer.unobserve(step2Ref.current);
      if (step3Ref.current) observer.unobserve(step3Ref.current);
      if (step4Ref.current) observer.unobserve(step4Ref.current);
    };
  }, []);

  // Scroll to specific step when clicking on sidebar links
  const scrollToStep = (stepId) => {
    const element = document.getElementById(`step${stepId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    // Close mobile sidebar when selecting a step
    setShowMobileSidebar(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header/>

      {/* Image Modal */}
      {imageModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={closeImageModal}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <button 
              className="absolute top-2 right-2 bg-white rounded-full p-2 text-gray-800 hover:text-gray-600 focus:outline-none"
              onClick={closeImageModal}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img 
              src={imageModal.imageSrc} 
              alt="Enlarged view"
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on the image itself
            />
          </div>
        </div>
      )}

      {/* Sticky Breadcrumb - shortened for mobile */}
      <div className="sticky top-0 z-10 bg-gray-100 py-2 sm:py-3 shadow-md">
        <div className="container mx-auto px-4 sm:px-6">
          <p className="text-xs sm:text-sm text-gray-600 text-left truncate">
            <a href="/competition" className="text-blue-600 hover:underline"> Competition</a> &gt; 
            <span className="hidden sm:inline"> NETS Click Payment Gateway </span>
            <span className="inline sm:hidden"> NETS Click</span> 
          </p>
        </div>
      </div>

      {/* Mobile Step Navigator - only visible on mobile */}
      <div className="md:hidden sticky top-10 z-10 bg-white shadow-md py-2 px-4">
        <div className="flex justify-between items-center">
          <div className="text-sm font-medium">
            Step {activeStep} of 4
          </div>
          <button 
            onClick={() => setShowMobileSidebar(!showMobileSidebar)}
            className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm"
          >
            {showMobileSidebar ? 'Hide Steps' : 'Show Steps'}
          </button>
        </div>
        
        {/* Mobile Sidebar - collapsible */}
        {showMobileSidebar && (
          <div className="mt-3 pb-2 border-t pt-2">
            <ul className="space-y-3 text-sm">
              {[1, 2, 3, 4].map(step => (
                <li 
                  key={step}
                  className={`${activeStep === step ? "text-blue-600 font-medium" : "text-gray-700"} 
                    text-left transition-colors duration-300 cursor-pointer hover:text-blue-500
                    py-1`}
                  onClick={() => scrollToStep(step)}
                >
                  Step {step} | {step === 1 ? "Setup account" :
                                 step === 2 ? "Create project" :
                                 step === 3 ? "Download resources" :
                                             "Start ideation"}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Main Content - responsive grid */}
      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-12 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8 text-left flex-grow">
        {/* Sidebar - hidden on mobile */}
        <aside className="hidden md:block bg-white rounded-lg shadow-md p-4 sm:p-6 text-left md:sticky md:top-16 self-start">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 text-left">In this Tutorial</h3>
          <ul className="space-y-4 text-sm text-left">
            <li 
              className={`${activeStep === 1 ? "text-blue-600 font-medium" : "text-black"} text-left transition-colors duration-300 cursor-pointer hover:text-blue-500`}
              onClick={() => scrollToStep(1)}
            >
              Step 1 | Setup OPENAPIPASS developer account
            </li>
            <li 
              className={`${activeStep === 2 ? "text-blue-600 font-medium" : "text-black"} text-left transition-colors duration-300 cursor-pointer hover:text-blue-500`}
              onClick={() => scrollToStep(2)}
            >
              Step 2 | Create your NETS Click project
            </li>
            <li 
              className={`${activeStep === 3 ? "text-blue-600 font-medium" : "text-black"} text-left transition-colors duration-300 cursor-pointer hover:text-blue-500`}
              onClick={() => scrollToStep(3)}
            >
              Step 3 | Download NETS Click resources
            </li>
            <li 
              className={`${activeStep === 4 ? "text-blue-600 font-medium" : "text-black"} text-left transition-colors duration-300 cursor-pointer hover:text-blue-500`}
              onClick={() => scrollToStep(4)}
            >
              Step 4 | Start your NETS Click ideation
            </li>
          </ul>
        </aside>
        
        {/* Main Section - takes full width on mobile */}
        <section className="col-span-1 md:col-span-2 text-left">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-3 sm:mb-4 text-left">NETS Click Payment Gateway Ideation</h2>
          <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 text-left">
            Follow the steps below to start your NETS Click payment gateway ideation journey for PolyFinTech100 API Hackathon!
          </p>

          {/* Step 1 */}
          <div id="step1" ref={step1Ref} data-step="1" className="scroll-mt-24 mb-6 sm:mb-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 sm:mb-4 text-left">Step 1 | Setup OPENAPIPASS developer account</h3>
            <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 text-left">
              Go to the website <a href="https://developers.openapipass.org/homepage" className="text-blue-600 hover:underline break-all">https://developers.openapipass.org/homepage</a> to log in to your developer account by entering your phone number. Click on 'Get SMS OTP'. On receiving the SMS OTP, click on 'Proceed' button.
            </p>
            
            <div className="flex flex-col items-center sm:items-start space-y-4 mb-4 sm:mb-6">
              <img
                src={DevelopersLogin}
                alt="OPENAPIPASS Login Page"
                className="shadow-md rounded-md w-full max-w-lg cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => openImageModal(DevelopersLogin)}
              />
              <img
                src={DevelopersSmsOtp}
                alt="OPENAPIPASS OTP Verification"
                className="shadow-md rounded-md w-full max-w-lg cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => openImageModal(DevelopersSmsOtp)}
              />
            </div>
          </div>
          
          {/* Step 2 */}
          <div id="step2" ref={step2Ref} data-step="2" className="scroll-mt-24 mb-6 sm:mb-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 sm:mb-4 text-left">Step 2 | Create your NETS Click sandbox project</h3>
            <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 text-left">
              Once logged in, navigate to the Sandbox environment and create a new project for NETS Click payment gateway integration. This will be your testing environment for developing innovative payment solutions.
            </p>
            <img
              src={DevelopersSandbox}
              alt="UAT Environment Selection"
              className="shadow-md rounded-md w-full max-w-lg cursor-pointer hover:opacity-90 transition-opacity mb-4"
              onClick={() => openImageModal(DevelopersSandbox)}
            />
            <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 text-left">
              Fill in the details like in the image below.
            </p>
            <img
              src={SandboxProjectDetails}
              alt="Industry Selection"
              className="shadow-md rounded-md w-full max-w-lg cursor-pointer hover:opacity-90 transition-opacity mb-4"
              onClick={() => openImageModal(SandboxProjectDetails)}
            />
            <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 text-left">
              Here, you would see that your sandbox project created, click on it to view your sandbox credentials.
            </p>
            <img
              src={SandboxProject}
              alt="Project Details Form"
              className="shadow-md rounded-md w-full max-w-lg cursor-pointer hover:opacity-90 transition-opacity mb-4"
              onClick={() => openImageModal(SandboxProject)}
            />
          </div>

          {/* Step 3 */}
          <div id="step3" ref={step3Ref} data-step="3" className="scroll-mt-24 mb-6 sm:mb-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 sm:mb-4 text-left">Step 3 | Download NETS Click resources and documentation</h3>
            <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 text-left">
              Click on the menu icon at the top left corner of the webpage to access the resources section.
            </p>
            <img
              src={DevelopersHamburgerMenu}
              alt="OPENAPIPASS Menu"
              className="shadow-md rounded-md w-full max-w-lg cursor-pointer hover:opacity-90 transition-opacity mb-4"
              onClick={() => openImageModal(DevelopersHamburgerMenu)}
            />
            <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 text-left">
              Navigate to 'Resources' &gt; 'Downloads' &gt; 'PolyFinTech' &gt; 'PayTech' to download your respective NETS Click Resources.
            </p>
            <img
              src={ResourceDownloadsSidebar}
              alt="Downloads Section"
              className="shadow-md rounded-md w-full max-w-lg cursor-pointer hover:opacity-90 transition-opacity mb-4"
              onClick={() => openImageModal(ResourceDownloadsSidebar)}
            />
            <img
              src={ResourcePolyFinTechPaytechNetsClick}
              alt="NETS Click Download Image"
              className="shadow-md rounded-md w-full max-w-lg cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => openImageModal(ResourcePolyFinTechPaytechNetsClick)}
            />
          </div>

          {/* Step 4 */}
          <div id="step4" ref={step4Ref} data-step="4" className="scroll-mt-24 mb-6 sm:mb-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 sm:mb-4 text-left">Step 4 | Start your NETS Click ideation journey</h3>
            <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 text-left">
              Create your own mock data for the competition with integrated NETS Payment Gateway.
            </p>
              <img
                src={NetsClickAddBankCard}
                alt="NETS Click Payment Flow"
                className="shadow-md rounded-md w-full max-w-[150px] cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => openImageModal(NetsClickAddBankCard)}
              />
              <br />
              <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 text-left">
                Note that NETS Click in Sandbox environment users simulated API responses. Hence, certification is not applicable in Sandbox environment.
              </p>
          </div>

          <div className="mt-6 sm:mt-10 text-left">
            <a 
              href="/competition" 
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 sm:py-3 px-4 sm:px-6 rounded-md text-sm sm:text-base font-medium transition duration-300"
            >
              Back to Competition
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default NetsClickIdeationLayout;